/**
 * BaseLayout - 公共布局组件
 * 提供顶部 Header（Logo、导航、语言切换、用户信息）和退出登录弹窗
 * AdminLayout 和 PortalLayout 共用此组件
 */
import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import request, { TOKEN_KEY } from "../../utils/request";
import { getHighestPriorityUserRole, SYSTEM_LOG_ROLES, userHasAnyRole } from "../../utils/user";
import { listMyStudySources } from "../../services/studySource";
import { useAuth } from "../../context/auth";
import ConfirmModal from "../common/ConfirmModal";
import logoImage from "../../assets/opsmate/logo.png";

interface BaseLayoutProps {
  children: React.ReactNode;
}

export default function BaseLayout({ children }: BaseLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { user, clearUser } = useAuth();

  const isZh = i18n.language === "zh";

  // 管理控制台可见权限：拥有系统日志相关角色
  const canAccessAdminArea = userHasAnyRole(user?.roles, SYSTEM_LOG_ROLES);
  // Study Source 可见权限：当前用户拥有已授权的 Study Source
  const activeUserId = user?.id ?? "";
  const [studySourceAccess, setStudySourceAccess] = useState({ userId: "", allowed: false });
  const canAccessStudySource =
    activeUserId !== "" && studySourceAccess.userId === activeUserId && studySourceAccess.allowed;

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setStudySourceAccess({ userId: "", allowed: false });
      return () => undefined;
    }
    const requestedUserId = user.id;

    void listMyStudySources()
      .then((result) => {
        if (!cancelled) {
          setStudySourceAccess({ userId: requestedUserId, allowed: result.data.length > 0 });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStudySourceAccess({ userId: requestedUserId, allowed: false });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  // 顶部标签激活态：根据当前路由前缀判定
  const activeSection = location.pathname.startsWith("/admin")
    ? "admin"
    : location.pathname.startsWith("/mvr")
      ? "mvr"
      : "ask";
  const topNavItems = [
    { key: "ask", label: "ASK", path: "/opsmate", visible: true },
    // { key: "mvr", label: "MVR-V3", path: "/study-source", visible: canAccessStudySource },
    { key: "admin", label: "Admin", path: "/admin/analytics", visible: canAccessAdminArea },
  ].filter((item) => item.visible);

  const userName = user?.name || "User";
  const userRole = getHighestPriorityUserRole(user?.roles);

  const toggleLanguage = () => {
    i18n.changeLanguage(isZh ? "en" : "zh");
  };

  // 点击外部关闭用户菜单
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 退出登录
  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await request.post("/_account/logout");
    } catch {
      // 即使接口失败也清除本地状态
    }
    localStorage.removeItem(TOKEN_KEY);
    clearUser();
    setLoggingOut(false);
    setUserMenuOpen(false);
    setLogoutConfirm(false);
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Top Header */}
      <header
        className="flex-shrink-0 z-10"
        style={{ backgroundColor: "#830051", position: "relative" }}
      >
        <div className="h-14 flex items-center justify-between">
          {/* Logo area */}
          <div
            className="w-48 flex-shrink-0 flex items-center px-2 cursor-pointer"
            onClick={() => navigate("/opsmate")}
          >
            <img src={logoImage} alt="AstraZeneca" className="h-full object-contain" />
          </div>
          {/* Nav + User info */}
          <div className="flex-1 flex items-center justify-between px-6">
            {/* Main Nav */}
            <nav className="flex items-center gap-1">
              {topNavItems.map((item) => {
                const isActive = activeSection === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => navigate(item.path)}
                    className="h-8 px-4 rounded-md text-sm font-medium cursor-pointer transition-all whitespace-nowrap"
                    style={{
                      backgroundColor: isActive ? "rgba(255,255,255,0.15)" : "transparent",
                      color: isActive ? "#ffffff" : "rgba(255,255,255,0.75)",
                      border: isActive
                        ? "1px solid rgba(255,255,255,0.3)"
                        : "1px solid transparent",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                          "rgba(255,255,255,0.1)";
                        (e.currentTarget as HTMLButtonElement).style.color = "#ffffff";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                          "transparent";
                        (e.currentTarget as HTMLButtonElement).style.color =
                          "rgba(255,255,255,0.75)";
                      }
                    }}
                  >
                    {item.label}
                  </button>
                );
              })}
            </nav>

            {/* Right side: Language toggle + User info */}
            <div className="flex items-center gap-4">
              {/* Language Toggle */}
              {/* <button
                type="button"
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/30 hover:bg-white/10 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-translate-2 text-white text-sm"></i>
                </div>
                <span className="text-sm font-medium text-white whitespace-nowrap">
                  {isZh ? 'EN' : '中文'}
                </span>
              </button> */}

              {/* User info + 退出登录 dropdown */}
              <div ref={userMenuRef} className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setUserMenuOpen((v) => !v);
                    setLogoutConfirm(false);
                  }}
                  className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white border border-white/30">
                    <i className="ri-shield-user-line text-base"></i>
                  </div>
                  <span className="text-sm text-white font-medium">{userName}</span>
                  {userRole && (
                    <span className="text-xs text-white/70 border border-white/30 rounded px-1.5 py-0.5">
                      {userRole}
                    </span>
                  )}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i
                      className={`ri-arrow-${userMenuOpen ? "up" : "down"}-s-line text-white/70 text-sm`}
                    ></i>
                  </div>
                </button>

                {userMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50">
                    <button
                      type="button"
                      onClick={() => {
                        setUserMenuOpen(false);
                        setLogoutConfirm(true);
                      }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                        <i className="ri-logout-box-r-line text-sm"></i>
                      </div>
                      {t("layout.logout")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <div style={{ backgroundColor: "#f8f8f8" }}>{children}</div>

      {/* 退出登录确认弹窗 */}
      <ConfirmModal
        open={logoutConfirm}
        title={t("layout.logout")}
        description={t("layout.logoutConfirm")}
        confirmText={loggingOut ? t("layout.loggingOut") : t("layout.confirmYes")}
        loading={loggingOut}
        onConfirm={handleLogout}
        onCancel={() => setLogoutConfirm(false)}
      />
    </div>
  );
}
